import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export interface SEOMetaProps {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_URL = "https://serenasquietrecoveryspace.com";
const SITE_NAME = "Serena's Quiet Recovery Space";
const DEFAULT_OG_IMAGE =
  "https://storage.readdy-site.link/project_files/1f18a76f-ed3f-4e88-bb38-b62c775c3336/dd8305a0-1fac-4e33-aa4a-430f46df46f4_ChatGPT-Image-Jun-9-2026-03_24_23-PM.png?v=ad55fcfd6c6876a070c9edc39cde37f1";
const DEFAULT_TWITTER = "summary_large_image";

export default function SEOMeta({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  noIndex = false,
  schema,
}: SEOMetaProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", ogTitle || title, true);
    setMeta("og:description", ogDescription || description, true);
    setMeta("og:url", `${BASE_URL}${canonicalUrl}`, true);
    setMeta("og:image", ogImage, true);
    setMeta("og:type", ogType, true);
    setMeta("og:site_name", SITE_NAME, true);
    setMeta("twitter:card", DEFAULT_TWITTER);
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
    setMeta("twitter:image", ogImage);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      canonicalEl.setAttribute("href", `${BASE_URL}${canonicalUrl}`);
    } else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", `${BASE_URL}${canonicalUrl}`);
      document.head.appendChild(link);
    }

    if (noIndex) {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) {
        robotsEl.setAttribute("content", "noindex, follow");
      } else {
        const meta = document.createElement("meta");
        meta.setAttribute("name", "robots");
        meta.setAttribute("content", "noindex, follow");
        document.head.appendChild(meta);
      }
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) {
        robotsEl.remove();
      }
    }

    if (schema) {
      const existingSchema = document.querySelectorAll('script[data-seo-schema]');
      existingSchema.forEach((el) => el.remove());

      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-schema", "");
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }
  }, [
    title,
    description,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    noIndex,
    schema,
    location.pathname,
  ]);

  return null;
}